import { ReactNode, useContext } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { fetchReportData } from 'api/reports';
import { ReportContext } from 'assets/js/pages/reports/ReportContext';
import { ReportTableProviderStrategy } from 'assets/js/pages/reports/ReportTableProviderStrategy';
import { uuidSingleton } from 'components/filter/filters-next/defaults';
import { transformSavedColumnFilters } from 'components/filter/filters-next/downstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { useTableSchema } from 'customHooks/useTableSchema';
import { dateToServerDateTime } from 'helpers/dates';
import { first } from 'lodash';
import { GivebutterColumnFilterState } from 'types/react-table';

const COLUMN_VISIBILITY_DEBOUNCE = 750;

const ReportTableProvider = (props: { children: ReactNode }) => {
    const { data: report } = useCurrentReport();
    const {
        sortColumn,
        sortDirection,
        chartType,
        chartColumn,
        dateRange,
        dateRangeType,
        setChartColumn,
        setChartType,
        setSortColumn,
        setSortDirection,
    } = useContext(ReportContext);
    const [startDate, endDate] = dateRange;
    const { children } = props;
    const resource = report.resource;
    const updateFilters = useFiltersCache(state => state.updateFiltersNextByKey);
    const { filterGroups } = useFiltersByKey('singleReport');
    const {
        data: { columns, graph },
    } = useTableSchema(resource);
    const visibleColumnsFromSchema = columns.reduce((prev, curr) => {
        prev[curr.name] = false;
        return prev;
    }, {} as VisibilityState);
    const visibleColumnsFromReport = report.query.columns.reduce((prev, curr) => {
        prev[curr.name] = true;
        return prev;
    }, {} as VisibilityState);
    const columnVisibilityState = {
        ...visibleColumnsFromSchema,
        ...visibleColumnsFromReport,
    };
    const columnFilters = report.query.columns.reduce((prev, curr) => {
        if (curr.filters) {
            prev[curr.name] = [
                {
                    id: uuidSingleton,
                    innerOperator: 'and',
                    outerOperator: 'and',
                    filters: transformSavedColumnFilters(curr.filters, resource, curr),
                },
            ];
        }
        return prev;
    }, {} as GivebutterColumnFilterState);

    return (
        <TableCoreResourceProvider
            filterCacheKey={'singleReport'}
            enableSchema={true}
            queryFn={(accountId, params) => fetchReportData(accountId, report.id, params)}
            initialState={{
                isTableFilterBarExpanded: true,
                sortingState: report.query.display_settings.order_by
                    ? [
                          {
                              id: report.query.display_settings.order_by,
                              desc: report.query.display_settings.order_by_direction === 'desc',
                          },
                      ]
                    : [],
                columnVisibilityState,
                columnOrderState: report.query.columns.map(col => col.name),
                givebutterColumnFiltersState: columnFilters,
            }}
            options={{
                enableHiding: true,
                enableSorting: true,
                enableGivebutterColumnFilters: true,
                meta: {
                    enableColumnOrder: true,
                    onHideColumn: (column, table) => {
                        if (sortColumn?.name === column.id) {
                            table.setSorting([]);
                            setSortColumn(undefined);
                        }

                        if (chartColumn?.name === column.id) {
                            setChartColumn(undefined);
                            setChartType(undefined);
                        }

                        updateFilters(
                            'singleReport',
                            filterGroups.filter(f =>
                                f.filters.filter(f => f.serverKey !== column.id),
                            ),
                        );
                    },
                    onVisualizeColumn: column => {
                        const col = columns.find(col => col.name === column.id);
                        const firstChartType = first(graph[column.id])?.type;
                        if (col) {
                            setChartColumn(col);
                            setChartType(firstChartType);
                        }
                    },
                    onSortColumn: (column, desc) => {
                        const col = columns.find(col => col.name === column.id);
                        if (col) {
                            setSortColumn(col);
                            setSortDirection(desc ? 'desc' : 'asc');
                        }
                    },
                },
            }}
            coreTableOptions={{
                tableStateOptions: {
                    debounce: { columnVisibility: COLUMN_VISIBILITY_DEBOUNCE },
                    props: { isPersisted: false },
                },
            }}
            extraParams={{
                date_range: {
                    type: dateRangeType,
                    range: [
                        dateToServerDateTime(startDate.startOf('day')),
                        dateToServerDateTime(endDate.endOf('day')),
                    ],
                },
                graph_settings: {
                    column: chartColumn ? chartColumn.name : null,
                    graph_type: chartColumn ? chartType : null,
                },
                display_settings: {
                    order_by: sortColumn ? sortColumn.name : null,
                    order_by_direction: sortColumn ? sortDirection : 'ASC',
                },
                timestamp_column: report.query.timestamp_column,
            }}
        >
            <ReportTableProviderStrategy>{children}</ReportTableProviderStrategy>
        </TableCoreResourceProvider>
    );
};

export { ReportTableProvider };
